import { useState } from 'react';
import { ProductCategory, categoryConfig, Product } from '../../../mocks/products';
import { agencyInfo } from '../../../mocks/dashboard';

interface AddProductModalProps {
  onClose: () => void;
  editProduct?: Product | null;
}

export default function AddProductModal({ onClose, editProduct }: AddProductModalProps) {
  const brand = agencyInfo.brandColor;
  const isEdit = !!editProduct;

  const [category, setCategory] = useState<ProductCategory>(editProduct?.category ?? 'Package');
  const [name, setName] = useState(editProduct?.name ?? '');
  const [destination, setDestination] = useState(editProduct?.destination ?? '');
  const [duration, setDuration] = useState(editProduct?.duration ?? '');
  const [description, setDescription] = useState(editProduct?.description ?? '');
  const [costPrice, setCostPrice] = useState(editProduct?.costPrice ?? 0);
  const [sellPrice, setSellPrice] = useState(editProduct?.sellPrice ?? 0);
  const [minPax, setMinPax] = useState(editProduct?.minPax ?? 1);
  const [maxPax, setMaxPax] = useState(editProduct?.maxPax ?? 10);
  const [status, setStatus] = useState(editProduct?.status ?? 'Active');
  const [inclusions, setInclusions] = useState<string[]>(editProduct?.inclusions ?? ['']);
  const [exclusions, setExclusions] = useState<string[]>(editProduct?.exclusions ?? ['']);
  const [supplierName, setSupplierName] = useState(editProduct?.supplierName ?? '');
  const [supplierContact, setSupplierContact] = useState(editProduct?.supplierContact ?? '');
  const [tags, setTags] = useState(editProduct?.tags.join(', ') ?? '');
  const [featured, setFeatured] = useState(editProduct?.featured ?? false);
  const [success, setSuccess] = useState(false);

  const margin = sellPrice > 0 ? Math.round(((sellPrice - costPrice) / sellPrice) * 100) : 0;
  const profit = sellPrice - costPrice;
  const fmtPrice = (n: number) => `${agencyInfo.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const categories: ProductCategory[] = ['Package', 'Flight', 'Hotel', 'Tour', 'Visa', 'Transfer', 'Insurance', 'Cruise'];

  const updateInclusion = (idx: number, val: string) => setInclusions((prev) => prev.map((v, i) => i === idx ? val : v));
  const updateExclusion = (idx: number, val: string) => setExclusions((prev) => prev.map((v, i) => i === idx ? val : v));
  const removeInclusion = (idx: number) => setInclusions((prev) => prev.filter((_, i) => i !== idx));
  const removeExclusion = (idx: number) => setExclusions((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(true);
    setTimeout(() => { setSuccess(false); onClose(); }, 2000);
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-2xl p-10 text-center max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${brand}15` }}>
            <i className="ri-checkbox-circle-line text-3xl" style={{ color: brand }} />
          </div>
          <h3 className="font-bold text-gray-900 text-lg mb-1">{isEdit ? 'Product Updated!' : 'Product Added!'}</h3>
          <p className="text-gray-400 text-sm">{name} has been {isEdit ? 'updated' : 'added'} to the catalog.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-bold text-gray-900 text-base">{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Category Selector */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Category</label>
              <div className="grid grid-cols-4 gap-2">
                {categories.map((cat) => {
                  const cfg = categoryConfig[cat];
                  const active = category === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-medium cursor-pointer transition-all ${active ? 'border-transparent' : 'border-gray-100 hover:border-gray-200'}`}
                      style={active ? { backgroundColor: cfg.bg, color: cfg.color, borderColor: cfg.color } : {}}
                    >
                      <i className={`${cfg.icon} text-lg`} style={active ? { color: cfg.color } : { color: '#9CA3AF' }} />
                      <span className={active ? '' : 'text-gray-500'}>{cat}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Product Name</label>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Maldives Luxury Honeymoon Package"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Destination</label>
                <input type="text" required value={destination} onChange={(e) => setDestination(e.target.value)}
                  placeholder="e.g. Maldives"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Duration</label>
                <input type="text" required value={duration} onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 7 Nights / 8 Days"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                <textarea required value={description} onChange={(e) => setDescription(e.target.value)}
                  rows={3} maxLength={500} placeholder="Describe the product in detail..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none" />
                <p className="text-xs text-gray-400 text-right mt-1">{description.length}/500</p>
              </div>
            </div>

            {/* Pricing */}
            <div className="border border-gray-100 rounded-xl p-4 space-y-4">
              <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Pricing ({agencyInfo.currency})</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Cost Price</label>
                  <input type="number" min="0" step="0.01" required value={costPrice}
                    onChange={(e) => setCostPrice(Number(e.target.value))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Sell Price</label>
                  <input type="number" min="0" step="0.01" required value={sellPrice}
                    onChange={(e) => setSellPrice(Number(e.target.value))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Profit / Margin</label>
                  <div className={`px-3 py-2.5 border rounded-xl text-sm font-semibold ${
                    margin >= 25 ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : margin >= 15 ? 'border-amber-200 bg-amber-50 text-amber-700'
                    : 'border-rose-200 bg-rose-50 text-rose-600'
                  }`}>
                    {profit > 0 ? `${fmtPrice(profit)} (${margin}%)` : '—'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Min Pax</label>
                  <input type="number" min="1" value={minPax} onChange={(e) => setMinPax(Number(e.target.value))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Max Pax</label>
                  <input type="number" min="1" value={maxPax} onChange={(e) => setMaxPax(Number(e.target.value))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
              </div>
            </div>

            {/* Inclusions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">Inclusions</label>
                <button type="button" onClick={() => setInclusions((p) => [...p, ''])}
                  className="text-xs font-medium cursor-pointer" style={{ color: brand }}>
                  <i className="ri-add-line" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {inclusions.map((inc, idx) => (
                  <div key={idx} className="flex gap-2">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0 mt-2.5">
                      <i className="ri-check-line text-xs text-emerald-600" />
                    </div>
                    <input type="text" value={inc} onChange={(e) => updateInclusion(idx, e.target.value)}
                      placeholder="e.g. Daily breakfast included"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none" />
                    {inclusions.length > 1 && (
                      <button type="button" onClick={() => removeInclusion(idx)}
                        className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-rose-400 cursor-pointer flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-sm" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Exclusions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">Exclusions</label>
                <button type="button" onClick={() => setExclusions((p) => [...p, ''])}
                  className="text-xs font-medium cursor-pointer" style={{ color: brand }}>
                  <i className="ri-add-line" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {exclusions.map((exc, idx) => (
                  <div key={idx} className="flex gap-2">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0 mt-2.5">
                      <i className="ri-close-line text-xs text-gray-400" />
                    </div>
                    <input type="text" value={exc} onChange={(e) => updateExclusion(idx, e.target.value)}
                      placeholder="e.g. International flights"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none" />
                    {exclusions.length > 1 && (
                      <button type="button" onClick={() => removeExclusion(idx)}
                        className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-rose-400 cursor-pointer flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-sm" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Supplier & Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Supplier Name</label>
                <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="e.g. Soneva Fushi Resort"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Supplier Email</label>
                <input type="email" value={supplierContact} onChange={(e) => setSupplierContact(e.target.value)}
                  placeholder="contact@supplier.com"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Tags (comma separated)</label>
                <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g. Luxury, Beach, Honeymoon"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive' | 'Sold Out')}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Sold Out">Sold Out</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={() => setFeatured((f) => !f)}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${featured ? '' : 'bg-gray-200'}`}
                    style={featured ? { backgroundColor: brand } : {}}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${featured ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Mark as Featured</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
              style={{ backgroundColor: brand }}>
              {isEdit ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
